/**
 * Client-side image compression utility
 * Resizes and compresses images to reduce file size while maintaining quality
 */

/**
 * Compresses an image file to reduce its size
 * @param {File} file - The image file to compress
 * @param {Object} options - Compression options
 * @param {number} options.maxWidth - Maximum width (default: 1500)
 * @param {number} options.maxHeight - Maximum height (default: 1500)
 * @param {number} options.quality - JPEG quality 0-1 (default: 0.8)
 * @param {string} options.outputFormat - Output format: 'jpeg', 'png', 'webp' (default: 'jpeg')
 * @returns {Promise<{dataUrl: string, blob: Blob, originalSize: number, compressedSize: number}>}
 */
export const compressImage = async (file, options = {}) => {
  const {
    maxWidth = 2000,
    maxHeight = 2000,
    quality = 1.0,
    outputFormat = 'jpeg'
  } = options;

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (event) => {
      const img = new Image();
      
      img.onload = () => {
        try {
          // Calculate new dimensions while maintaining aspect ratio
          const { width: newWidth, height: newHeight } = calculateDimensions(
            img.width, 
            img.height, 
            maxWidth, 
            maxHeight
          );
          
          // Create canvas for compression
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          canvas.width = newWidth;
          canvas.height = newHeight;
          
          // Enable image smoothing for better quality
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          
          // Draw and compress the image
          ctx.drawImage(img, 0, 0, newWidth, newHeight);
          
          // Convert to blob with specified quality
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Failed to compress image'));
                return;
              }
              
              // Also get data URL for immediate use
              const dataUrl = canvas.toDataURL(`image/${outputFormat}`, quality);
              
              resolve({
                dataUrl,
                blob,
                originalSize: file.size,
                compressedSize: blob.size,
                originalDimensions: { width: img.width, height: img.height },
                newDimensions: { width: newWidth, height: newHeight },
                compressionRatio: ((file.size - blob.size) / file.size * 100).toFixed(1)
              });
            },
            `image/${outputFormat}`,
            quality
          );
        } catch (error) {
          reject(error);
        }
      };
      
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = event.target.result;
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

/**
 * Calculate optimal dimensions while maintaining aspect ratio
 * @param {number} originalWidth 
 * @param {number} originalHeight 
 * @param {number} maxWidth 
 * @param {number} maxHeight 
 * @returns {Object} New dimensions
 */
const calculateDimensions = (originalWidth, originalHeight, maxWidth, maxHeight) => {
  // If image is already smaller than max dimensions, keep original size
  if (originalWidth <= maxWidth && originalHeight <= maxHeight) {
    return { width: originalWidth, height: originalHeight };
  }
  
  // Calculate aspect ratio
  const aspectRatio = originalWidth / originalHeight;
  
  let newWidth = maxWidth;
  let newHeight = maxHeight;
  
  // Determine which dimension is the limiting factor
  if (aspectRatio > 1) {
    // Landscape: width is larger
    newHeight = newWidth / aspectRatio;
    if (newHeight > maxHeight) {
      newHeight = maxHeight;
      newWidth = newHeight * aspectRatio;
    }
  } else {
    // Portrait or square: height is larger or equal
    newWidth = newHeight * aspectRatio;
    if (newWidth > maxWidth) {
      newWidth = maxWidth;
      newHeight = newWidth / aspectRatio;
    }
  }
  
  return {
    width: Math.round(newWidth),
    height: Math.round(newHeight)
  };
};

/**
 * Format file size for display
 * @param {number} bytes 
 * @returns {string} Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Check if file size exceeds limit
 * @param {number} fileSizeBytes 
 * @param {number} limitMB - Limit in megabytes (default: 10)
 * @returns {boolean}
 */
export const exceedsFileLimit = (fileSizeBytes, limitMB = 10) => {
  const limitBytes = limitMB * 1024 * 1024;
  return fileSizeBytes > limitBytes;
};

/**
 * Estimate compressed file size based on dimensions and quality
 * This is a rough estimate for JPEG files
 * @param {number} width 
 * @param {number} height 
 * @param {number} quality 
 * @returns {number} Estimated size in bytes
 */
export const estimateCompressedSize = (width, height, quality = 1.0) => {
  // Rough estimate: JPEG compression ratio based on quality and pixel count
  const pixels = width * height;
  const baseCompressionRatio = quality * 0.1; // Base ratio (higher quality = larger file)
  const complexityFactor = Math.min(pixels / (1000 * 1000), 4); // Account for image complexity
  
  // Estimated bytes per pixel for JPEG (varies with quality and complexity)
  const bytesPerPixel = baseCompressionRatio + (complexityFactor * 0.05);
  
  return Math.round(pixels * bytesPerPixel);
};

/**
 * Get optimal compression settings to target a specific file size
 * Prioritizes maintaining 2000px max dimension for good D&D map quality
 * @param {number} originalWidth 
 * @param {number} originalHeight 
 * @param {number} targetSizeBytes 
 * @returns {Object} Optimized settings
 */
export const getOptimalSettings = (originalWidth, originalHeight, targetSizeBytes = 9 * 1024 * 1024) => {
  // Start with 2000px max dimension and maximum quality
  let quality = 1.0;
  let maxDimension = Math.min(Math.max(originalWidth, originalHeight), 2000); // Start at 2000px max
  
  // Try to find optimal settings through iteration
  for (let attempt = 0; attempt < 10; attempt++) {
    const { width: newWidth, height: newHeight } = calculateDimensions(
      originalWidth, 
      originalHeight, 
      maxDimension, 
      maxDimension
    );
    
    const estimatedSize = estimateCompressedSize(newWidth, newHeight, quality);
    
    if (estimatedSize <= targetSizeBytes) {
      return {
        maxWidth: maxDimension,
        maxHeight: maxDimension,
        quality: Math.max(quality, 0.85), // Never go below 85% quality for D&D maps
        outputFormat: 'jpeg'
      };
    }
    
    // Reduce quality first, then dimensions if needed
    if (quality > 0.9) {
      quality -= 0.05;
    } else if (maxDimension > 1600) {
      maxDimension = Math.round(maxDimension * 0.9);
      quality = 1.0; // Reset quality when reducing dimensions
    } else {
      quality -= 0.05;
    }
  }
  
  // Fallback to conservative settings but still prioritize dimensions
  return {
    maxWidth: 1600,
    maxHeight: 1600,
    quality: 0.9,
    outputFormat: 'jpeg'
  };
};

/**
 * Get recommended compression settings based on file size
 * Target: Stay under 10MB Cloudinary limit while preserving maximum quality
 * Maximum long edge: 2000px to maintain good quality for D&D maps
 * @param {number} fileSizeBytes 
 * @returns {Object} Recommended settings
 */
export const getRecommendedSettings = (fileSizeBytes) => {
  const sizeMB = fileSizeBytes / (1024 * 1024);
  
  if (sizeMB < 2) {
    // Small files - minimal compression, preserve maximum quality
    return { maxWidth: 2000, maxHeight: 2000, quality: 1.0, outputFormat: 'jpeg' };
  } else if (sizeMB < 5) {
    // Medium files - light compression
    return { maxWidth: 2000, maxHeight: 2000, quality: 1.0, outputFormat: 'jpeg' };
  } else if (sizeMB < 15) {
    // Large files - moderate compression with 2000px max dimension
    return { maxWidth: 2000, maxHeight: 2000, quality: 1.0, outputFormat: 'jpeg' };
  } else if (sizeMB < 25) {
    // Very large files - stronger compression but keep 2000px max
    return { maxWidth: 2000, maxHeight: 2000, quality: 0.95, outputFormat: 'jpeg' };
  } else {
    // Extremely large files - more aggressive quality reduction but keep dimensions reasonable
    return { maxWidth: 1800, maxHeight: 1800, quality: 0.9, outputFormat: 'jpeg' };
  }
}; 